<script setup lang="ts">
import { BeamConstancyWorkItemDto, BeamConstancyWorkStepDto } from '@/api/generator';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import Attachments from '@/components/forms/Attachments/Attachments.vue';
import Textarea from '@/components/forms/Textarea/Textarea.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import MeasurementDialog from '@/components/measurement/MeasurementDialog.vue';
import MeasurementSequence from '@/components/measurement/MeasurementSequence.vue';
import SingleMeasurement from '@/components/measurement/SingleMeasurement.vue';
import { useBeamConstancyCheckTask } from '@/components/measurement/utils/beamConstancyCheck';
import NamedGroup from '@/components/NamedGroup/NamedGroup.vue';
import Notification from '@/components/notifications/Notification.vue';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import Button from '@/components/buttons/Button.vue';
import { truthy } from '@/utils/helper';
import { useReferenceMeasurementInfo } from '@/utils/useReferenceMeasurementInfo';
import { useToggle } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { computed, ref, toRef } from 'vue';
import { useJobOccurrence } from '../utils/useJobOccurrence';
import JobExecutionWorkSteps from './JobExecutionWorkSteps.vue';
import BCCworkItemPanel from './workItems/BCCworkItemPanel.vue';

const { t } = useTranslation();

const { getNotification } = useReferenceMeasurementInfo(t);

const props = defineProps<{
  workitem: BeamConstancyWorkItemDto;
}>();

const mode = useFormMode();

const {
  discardMeasurement,
  reload,
  toggleSkipWorkstep,
  areAllWorkstepsSkipped,
  toggleSkipAllWorksteps,
  isSkippAllPossible,
} = useJobOccurrence();

const { measurementSequence, getMeasurement, supportSystemGroups, numGroups } =
  useBeamConstancyCheckTask(toRef(() => props.workitem));

const [measurementDialogOpen, toggleMeasurementDialog] = useToggle(false);

const optimisticSkipAllState = ref<boolean | null>(null);

const isSingleMeasurement = computed(() => selectedMeasurements.value?.length === 1);

const isMeasurementSequenceExecutable = computed(() => {
  // if we have an optimistic value (skip all operation is in progress), use that immediately
  if (optimisticSkipAllState.value !== null) {
    return !optimisticSkipAllState.value;
  }

  return !props.workitem.workSteps?.every(
    (workstep) => !workstep.isExecutable || workstep.workState === 'Skipped',
  );
});

const selectedWorkSteps = ref<BeamConstancyWorkStepDto[] | undefined>(undefined);

const selectedMeasurements = computed(() => {
  return selectedWorkSteps.value?.map((step) => getMeasurement(step)).filter(truthy);
});

const measurementDialogTitle = computed(() =>
  isSingleMeasurement.value
    ? t('SingleMeasurementDialogTitle')
    : t('SequenceMeasurementDialogTitle'),
);

async function startMeasurementSequence() {
  selectedWorkSteps.value = props.workitem.workSteps ?? undefined;
  toggleMeasurementDialog(true);
}

async function startSingleMeasurement(workStep: BeamConstancyWorkStepDto) {
  if (workStep.workState === 'Skipped') {
    await toggleSkipWorkstep(workStep);
  }

  selectedWorkSteps.value = [workStep];
  toggleMeasurementDialog(true);
}

async function optimisticToggleSkipAllWorksteps(workitem: BeamConstancyWorkItemDto, skip: boolean) {
  optimisticSkipAllState.value = skip;

  try {
    await toggleSkipAllWorksteps(workitem, skip);
    optimisticSkipAllState.value = null;
  } catch (error) {
    optimisticSkipAllState.value = areAllWorkstepsSkipped(workitem) ?? false;
    throw error;
  }
}
</script>

<template>
  <MeasurementDialog
    v-if="measurementDialogOpen"
    v-model:open="measurementDialogOpen"
    :measurements="selectedMeasurements"
    :title="measurementDialogTitle"
    @measurement:finished="reload"
  >
    <template #measurements="{ measurement, results, status, finished }">
      <SingleMeasurement
        v-if="isSingleMeasurement"
        :measurement="measurement.value"
        :status="status.value"
        :result="results[0]"
      />

      <MeasurementSequence
        v-else
        class="mt-4 mb-4"
        :measurements="measurementSequence || []"
        :results="results"
        :status="status.value"
        :finished="finished"
        :currentMeasurement="measurement.value"
      />
    </template>

    <template #actions="{ measurement, close, repeat, isRepeatable }">
      <div class="d-flex justify-end">
        <div>
          <Button
            v-if="isRepeatable"
            variant="text"
            :label="$t('Repeat')"
            class="mt-4 mr-2"
            @click="
              discardMeasurement(measurement.value);
              repeat();
            "
          />
        </div>

        <Button variant="secondary" :label="$t('Close')" class="mt-4 mr-2" @click="close()" />
      </div>
    </template>
  </MeasurementDialog>

  <v-row
    v-if="
      numGroups ||
      workitem.instruction?.textAreaContent ||
      workitem.instruction?.attachments?.length
    "
  >
    <v-col v-if="numGroups" cols="8">
      <FormLabel class="mb-2" :label="$t('DosimetryAndQaEquipment')">
        <NamedGroup :groups="supportSystemGroups">
          <template #default="group">
            <v-row dense class="mt-2">
              <v-col
                v-for="supportSystem in group.items"
                :key="supportSystem.id"
                cols="12"
                lg="6"
                xxl="4"
                class="mb-4"
              >
                <RTSystemTile class="w-100 h-100" :item="supportSystem" />
              </v-col>
            </v-row>
          </template>
        </NamedGroup>
      </FormLabel>
    </v-col>

    <v-col
      v-if="workitem.instruction?.textAreaContent || workitem.instruction?.attachments?.length"
      cols="4"
    >
      <FormLabel :label="$t('Instruction_one')">
        <Textarea
          v-if="workitem.instruction?.textAreaContent"
          :modelValue="workitem.instruction.textAreaContent"
          mode="details"
        />
        <v-divider
          v-if="workitem.instruction?.textAreaContent && workitem.instruction?.attachments?.length"
          color="primary-lighten-4"
          class="mt-1 mb-1"
          thickness="1"
        ></v-divider>
        <Attachments
          v-if="workitem.instruction?.attachments?.length"
          :modelValue="workitem.instruction?.attachments"
          mode="details"
        />
      </FormLabel>
    </v-col>
  </v-row>

  <v-row v-if="mode === 'execution' && (workitem.workSteps?.length ?? 0) > 1">
    <v-col class="d-flex justify-end align-center ga-4">
      <v-checkbox
        v-if="isSkippAllPossible(workitem)"
        :label="$t('JobExecutionSkipAll')"
        density="compact"
        hideDetails
        class="mr-2"
        :modelValue="areAllWorkstepsSkipped(workitem)"
        @click.stop="optimisticToggleSkipAllWorksteps(workitem, !areAllWorkstepsSkipped(workitem))"
      />

      <Button
        v-if="measurementSequence?.length && mode === 'execution'"
        variant="secondary"
        class="mr-3 text-right"
        size="small"
        :disabled="!isMeasurementSequenceExecutable"
        :label="$t('StartSequence')"
        @click="startMeasurementSequence()"
      />
    </v-col>
  </v-row>
  <v-row>
    <v-col>
      <JobExecutionWorkSteps class="mt-4" :workSteps="workitem.workSteps || undefined">
        <template #notification="{ step }">
          <Notification
            class="mr-3"
            :value="
              getNotification(step.combination?.referenceMeasurementInfo?.referenceMeasurementState)
            "
          />
        </template>

        <template #actions="{ step }">
          <Button
            v-if="mode === 'execution'"
            variant="secondary"
            :disabled="!step.isExecutable"
            class="mr-3"
            size="small"
            :label="$t('Start')"
            @click.stop.prevent="startSingleMeasurement(step)"
          />
        </template>

        <template #workitem="{ step }">
          <BCCworkItemPanel :step="step" />
        </template>
      </JobExecutionWorkSteps>
    </v-col>
  </v-row>
</template>
